import { DateTime } from "luxon";
import type { RosterActivityEvent } from "@/features/roster-activity";
import { COHORT_LABELS, KIND_LABELS } from "@/features/roster-activity";

export type RosterCopyFormat = "text" | "csv" | "tsv";

export type RosterCopyOptions = {
  format: RosterCopyFormat;
  includeDates: boolean;
};

const TABLE_HEADERS = ["First Name", "Last Name", "PGA ID", "Facility", "Change", "Detail", "Program Type"] as const;

const EMPTY_PERIOD_COPY = "No roster activity in this period.";

function activityHeadline(event: RosterActivityEvent): string {
  return event.headline.replace(`${event.personName} `, "");
}

function formatOccurredAt(occurredAt: string): string {
  return DateTime.fromISO(occurredAt).toFormat("MMM d, yyyy");
}

function escapeDelimited(value: string, delimiter: "," | "\t"): string {
  const needsQuotes = delimiter === "," ? /[",\n\r]/.test(value) : /["\t\n\r]/.test(value);
  if (!needsQuotes) return value;
  return `"${value.replace(/"/g, '""')}"`;
}

/** Freeform detail when present; otherwise the person-stripped headline for context. */
function delimitedDetail(event: RosterActivityEvent): string {
  const detail = event.detail?.trim();
  if (detail) return detail;
  return activityHeadline(event);
}

function displayFirstName(event: RosterActivityEvent): string {
  return event.firstName?.trim() || event.personName.trim().split(/\s+/)[0] || "";
}

function displayLastName(event: RosterActivityEvent): string {
  if (event.lastName?.trim()) return event.lastName.trim();
  const parts = event.personName.trim().split(/\s+/);
  return parts.length > 1 ? parts.slice(1).join(" ") : "";
}

function buildTextCopy(events: RosterActivityEvent[], includeDates: boolean): string {
  if (!events.length) return EMPTY_PERIOD_COPY;

  return events
    .map((event) => {
      const headline = activityHeadline(event);
      const detail = event.detail ? ` (${event.detail})` : "";
      const facility = event.facilityName ? ` — ${event.facilityName}` : "";
      const body = `${displayFirstName(event)} ${displayLastName(event)} — ${headline}${detail}${facility}`.trim();
      if (!includeDates) return body;
      return `${formatOccurredAt(event.occurredAt)} — ${body}`;
    })
    .join("\n");
}

function buildRosterTableAoA(events: RosterActivityEvent[], includeDates: boolean): string[][] {
  if (!events.length) return [[EMPTY_PERIOD_COPY]];

  const headers = [...(includeDates ? ["Date"] : []), ...TABLE_HEADERS];
  const rows = events.map((event) => [
    ...(includeDates ? [formatOccurredAt(event.occurredAt)] : []),
    displayFirstName(event),
    displayLastName(event),
    event.pgaId ?? "",
    event.facilityName ?? "",
    KIND_LABELS[event.kind],
    delimitedDetail(event),
    COHORT_LABELS[event.cohort]
  ]);

  return [headers, ...rows];
}

function buildDelimitedCopy(events: RosterActivityEvent[], includeDates: boolean, delimiter: "," | "\t"): string {
  const table = buildRosterTableAoA(events, includeDates);
  if (table.length === 1 && table[0]?.[0] === EMPTY_PERIOD_COPY) return EMPTY_PERIOD_COPY;

  return table.map((row) => row.map((cell) => escapeDelimited(cell, delimiter)).join(delimiter)).join("\n");
}

export function buildRosterCopy(events: RosterActivityEvent[], options: RosterCopyOptions): string {
  if (options.format === "csv") {
    return buildDelimitedCopy(events, options.includeDates, ",");
  }
  if (options.format === "tsv") {
    return buildDelimitedCopy(events, options.includeDates, "\t");
  }
  return buildTextCopy(events, options.includeDates);
}

/** Preview of the clipboard payload for the dialog (first few rows). */
export function buildRosterCopyPreview(events: RosterActivityEvent[], options: RosterCopyOptions, maxRows = 3): string {
  if (!events.length) {
    return buildRosterCopy([], options);
  }
  return buildRosterCopy(events.slice(0, maxRows), options);
}

export function rosterCopySuccessMessage(format: RosterCopyFormat): string {
  if (format === "csv") return "CSV copied";
  if (format === "tsv") return "Copied for Sheets / Excel";
  return "List copied";
}

export function rosterDownloadSuccessMessage(format: RosterCopyFormat): string {
  if (format === "csv") return "CSV downloaded";
  if (format === "tsv") return "Excel file downloaded";
  return "List downloaded";
}

export function rosterDownloadFilename(format: RosterCopyFormat): string {
  const stamp = DateTime.now().toFormat("yyyy-MM-dd");
  if (format === "csv") return `roster-activity-${stamp}.csv`;
  if (format === "tsv") return `roster-activity-${stamp}.xlsx`;
  return `roster-activity-${stamp}.txt`;
}

export function rosterDownloadMimeType(format: RosterCopyFormat): string {
  if (format === "csv") return "text/csv;charset=utf-8";
  if (format === "tsv") return "text/tab-separated-values;charset=utf-8";
  return "text/plain;charset=utf-8";
}

export async function downloadRosterCopy(events: RosterActivityEvent[], options: RosterCopyOptions): Promise<void> {
  if (options.format === "tsv") {
    const XLSX = await import("xlsx");
    const worksheet = XLSX.utils.aoa_to_sheet(buildRosterTableAoA(events, options.includeDates));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Roster Activity");
    XLSX.writeFile(workbook, rosterDownloadFilename("tsv"));
    return;
  }

  const content = buildRosterCopy(events, options);
  const blob = new Blob([content], { type: rosterDownloadMimeType(options.format) });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = rosterDownloadFilename(options.format);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** @deprecated Prefer buildRosterCopy({ format: "text", includeDates: false }) */
export function buildPlainListCopy(events: RosterActivityEvent[]): string {
  return buildRosterCopy(events, { format: "text", includeDates: false });
}
